<template>
  <themed-modal
    name="document-preview-modal"
    :title="modalTitle"
    size="xl"
    :click-to-close="true"
    @opened="handleOpened"
    @closed="handleClosed"
  >
    <div class="document-preview-container">
      <!-- Loading State -->
      <div v-if="isLoading" class="document-preview-loading">
        <svg class="animate-spin w-8 h-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p>Loading preview...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="document-preview-error">
        <theme-icon icon="alert-circle" type="icons" classes="w-12 h-12" />
        <h3>Preview Not Available</h3>
        <p>{{ errorMessage }}</p>
        <themed-button
          variant="default"
          @click="handleDownload"
          class="document-preview-download-button"
        >
          <theme-icon icon="download" type="icons" classes="w-4 h-4" />
          <span>Download File</span>
        </themed-button>
      </div>

      <!-- Image Preview -->
      <div v-else-if="isImage" class="document-preview-image" role="img" :aria-label="`Preview of ${document.name}`">
        <img
          :src="document.view_url"
          :alt="`Full size preview of ${document.name}`"
          @load="handleImageLoad"
          @error="handlePreviewError"
        />
      </div>

      <!-- PDF Preview -->
      <div v-else-if="isPDF" class="document-preview-pdf">
        <iframe
          :src="document.preview_url"
          :title="`PDF preview of ${document.name}`"
          :aria-label="`PDF document preview: ${document.name}`"
          @load="handleIframeLoad"
          @error="handlePreviewError"
        ></iframe>
      </div>

      <!-- Video Preview -->
      <div v-else-if="isVideo" class="document-preview-video">
        <video
          controls
          :src="document.view_url"
          :aria-label="`Video: ${document.name}`"
          @loadeddata="handleVideoLoad"
          @error="handlePreviewError"
        >
          Your browser does not support the video tag.
        </video>
      </div>

      <!-- Unsupported File Type -->
      <div v-else class="document-preview-unsupported">
        <theme-icon icon="file" type="icons" classes="w-16 h-16" />
        <h3>Preview Not Available</h3>
        <p>This file type cannot be previewed in the browser.</p>
        <div class="document-preview-file-info">
          <div class="document-preview-file-info-item">
            <span class="label">File Name:</span>
            <span class="value">{{ document.name }}</span>
          </div>
          <div class="document-preview-file-info-item">
            <span class="label">File Type:</span>
            <span class="value">{{ document.type ? document.type.toUpperCase() : 'Unknown' }}</span>
          </div>
          <div class="document-preview-file-info-item">
            <span class="label">File Size:</span>
            <span class="value">{{ document.readable_size }}</span>
          </div>
        </div>
        <themed-button
          variant="default"
          @click="handleDownload"
          class="document-preview-download-button"
        >
          <theme-icon icon="download" type="icons" classes="w-4 h-4" />
          <span>Download File</span>
        </themed-button>
      </div>
    </div>
  </themed-modal>
</template>

<script>
export default {
  name: 'DocumentPreviewModal',
  
  props: {
    document: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      isLoading: true,
      hasError: false,
      errorMessage: '',
      previouslyFocusedElement: null
    };
  },
  
  computed: {
    /**
     * Get modal title
     */
    modalTitle() {
      return this.document ? this.document.name : 'Document Preview';
    },
    
    /**
     * Check if document is an image
     */
    isImage() {
      return this.document && this.document.is_image === true;
    },
    
    /**
     * Check if document is a PDF
     */
    isPDF() {
      return this.document && this.document.type === 'pdf';
    },
    
    /**
     * Check if document is a video
     */
    isVideo() {
      return this.document && this.document.is_video === true;
    },
    
    /**
     * Check if preview is supported
     */
    isPreviewSupported() {
      return this.isImage || this.isPDF || this.isVideo;
    }
  },
  
  watch: {
    /**
     * Watch for document changes
     */
    document: {
      handler(newDocument) {
        if (newDocument) {
          this.resetState();
          this.checkPreviewAvailability();
        }
      },
      immediate: true
    }
  },
  
  methods: {
    /**
     * Handle modal opened event
     */
    handleOpened() {
      // Store the element that had focus before modal opened
      this.previouslyFocusedElement = document.activeElement;
      
      this.resetState();
      this.checkPreviewAvailability();
      
      // Set up focus trap
      this.setupFocusTrap();
    },
    
    /**
     * Handle modal closed event
     */
    handleClosed() {
      this.resetState();
      
      // Restore focus to previously focused element
      if (this.previouslyFocusedElement) {
        this.$nextTick(() => {
          this.previouslyFocusedElement.focus();
        });
      }
      
      // Clean up focus trap
      this.removeFocusTrap();
    },
    
    /**
     * Reset component state
     */
    resetState() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';
    },
    
    /**
     * Check if preview is available for this document
     */
    checkPreviewAvailability() {
      if (!this.document) {
        this.hasError = true;
        this.errorMessage = 'Document information is missing.';
        this.isLoading = false;
        return;
      }
      
      // Check if preview is supported
      if (!this.isPreviewSupported) {
        this.isLoading = false;
        // Automatically trigger download for unsupported types
        this.$nextTick(() => {
          // Don't auto-download, just show the unsupported message
          // User can click download button if they want
        });
        return;
      }
      
      // Check if required URLs are available
      if (this.isImage && !this.document.view_url) {
        this.hasError = true;
        this.errorMessage = 'Image URL is not available.';
        this.isLoading = false;
        return;
      }
      
      if (this.isPDF && !this.document.preview_url) {
        this.hasError = true;
        this.errorMessage = 'PDF preview URL is not available.';
        this.isLoading = false;
        return;
      }
      
      if (this.isVideo && !this.document.view_url) {
        this.hasError = true;
        this.errorMessage = 'Video URL is not available.';
        this.isLoading = false;
        return;
      }
      
      // If we get here, preview should be available
      // Loading state will be cleared by load handlers
    },
    
    /**
     * Handle image load success
     */
    handleImageLoad() {
      this.isLoading = false;
      this.hasError = false;
    },
    
    /**
     * Handle iframe load success
     */
    handleIframeLoad() {
      this.isLoading = false;
      this.hasError = false;
    },
    
    /**
     * Handle video load success
     */
    handleVideoLoad() {
      this.isLoading = false;
      this.hasError = false;
    },
    
    /**
     * Handle preview load error
     */
    handlePreviewError(event) {
      console.error('Preview load error:', event);
      this.isLoading = false;
      this.hasError = true;
      this.errorMessage = 'Failed to load preview. The file may be corrupted or unavailable.';
    },
    
    /**
     * Handle download button click
     */
    handleDownload() {
      if (this.document && this.document.download_url) {
        window.location.href = this.document.download_url;
        
        // Show notification
        this.$notify({
          type: 'info',
          title: 'Download',
          text: `Downloading ${this.document.name}...`
        });
      } else {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Download URL is not available.'
        });
      }
    },
    
    /**
     * Set up focus trap for modal
     */
    setupFocusTrap() {
      this.handleKeyDown = (e) => {
        if (e.key === 'Tab') {
          const modal = this.$el;
          if (!modal) return;
          
          const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
          );
          
          if (focusableElements.length === 0) return;
          
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];
          
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };
      
      document.addEventListener('keydown', this.handleKeyDown);
    },
    
    /**
     * Remove focus trap
     */
    removeFocusTrap() {
      if (this.handleKeyDown) {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    }
  }
};
</script>
